import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";

import CoverLayout from "layouts/authentication/components/CoverLayout";
import { useAuth } from "contexts/AuthContext";

const bgImage =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80";

function EnhancedSignUp() {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    accountType: "individual",
    organizationName: "",
    agreeTerms: false,
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, agreeTerms: e.target.checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.firstName || !formData.lastName) {
      setError("First and last name are required");
      return;
    }
    if (!formData.email || !formData.password) {
      setError("Email and password are required");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (formData.accountType === "organization" && !formData.organizationName) {
      setError("Organization name is required");
      return;
    }
    if (!formData.agreeTerms) {
      setError("You must agree to the terms and conditions");
      return;
    }

    setLoading(true);
    try {
      const result = await signup({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        company: formData.accountType === "organization" ? formData.organizationName : "",
      });

      if (result.success) {
        setSuccess("Account created! Redirecting to dashboard...");
        setTimeout(() => navigate("/dashboard"), 1500);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react/prop-types
  const AccountTypeCard = ({ type, title, description, icon }) => {
    const isSelected = formData.accountType === type;
    return (
      <ArgonBox
        onClick={() => setFormData((prev) => ({ ...prev, accountType: type, organizationName: "" }))}
        sx={{
          border: isSelected ? "2px solid #5046e5" : "2px solid #e0e0e0",
          borderRadius: "12px",
          padding: "20px 16px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          backgroundColor: isSelected ? "rgba(80, 70, 229, 0.04)" : "transparent",
          "&:hover": {
            borderColor: isSelected ? "#5046e5" : "#bdbdbd",
          },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <ArgonBox
          sx={{
            width: 44,
            height: 44,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isSelected ? "rgba(80, 70, 229, 0.1)" : "#f5f5f5",
            mb: 1.5,
            fontSize: "1.3rem",
          }}
        >
          {icon}
        </ArgonBox>
        <ArgonTypography variant="button" fontWeight="bold" sx={{ mb: 0.5 }}>
          {title}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="text" sx={{ lineHeight: 1.4 }}>
          {description}
        </ArgonTypography>
      </ArgonBox>
    );
  };

  return (
    <CoverLayout
      title="Get Started"
      description="Create your PRM Core account"
      image={bgImage}
      imgPosition="top"
      button={{ color: "dark", variant: "gradient" }}
    >
      <Card>
        <ArgonBox pt={3} pb={1} px={3} textAlign="center">
          <ArgonTypography variant="h5" fontWeight="bold">
            Create Your Account
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
            Start managing your portfolio in minutes
          </ArgonTypography>
        </ArgonBox>

        <ArgonBox pt={2} pb={3} px={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }}>
              {success}
            </Alert>
          )}

          <ArgonBox component="form" role="form" onSubmit={handleSubmit}>
            {/* Account Type Selection */}
            <ArgonBox mb={3}>
              <ArgonTypography variant="caption" fontWeight="bold" color="text" sx={{ mb: 1.5, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Account Type
              </ArgonTypography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <AccountTypeCard
                    type="individual"
                    title="Individual"
                    description="Personal account with full data isolation"
                    icon={<span role="img" aria-label="person">&#128100;</span>}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AccountTypeCard
                    type="organization"
                    title="Organization"
                    description="Shared access for your team"
                    icon={<span role="img" aria-label="team">&#127970;</span>}
                  />
                </Grid>
              </Grid>
            </ArgonBox>

            {/* Organization Name (conditional) */}
            <Collapse in={formData.accountType === "organization"}>
              <ArgonBox mb={2}>
                <ArgonTypography variant="caption" fontWeight="bold" color="text" sx={{ mb: 0.5, display: "block" }}>
                  Organization Name
                </ArgonTypography>
                <ArgonInput
                  name="organizationName"
                  placeholder="Acme Property Group"
                  value={formData.organizationName}
                  onChange={handleInputChange}
                  size="large"
                />
              </ArgonBox>
            </Collapse>

            {/* Name Fields */}
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="bold" color="text" sx={{ mb: 0.5, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Your Details
              </ArgonTypography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <ArgonInput
                    name="firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    size="large"
                  />
                </Grid>
                <Grid item xs={6}>
                  <ArgonInput
                    name="lastName"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    size="large"
                  />
                </Grid>
              </Grid>
            </ArgonBox>

            {/* Email & Phone */}
            <ArgonBox mb={2}>
              <ArgonInput
                name="email"
                type="email"
                placeholder="Work Email"
                value={formData.email}
                onChange={handleInputChange}
                size="large"
              />
            </ArgonBox>
            <ArgonBox mb={2}>
              <ArgonInput
                name="phone"
                type="tel"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                size="large"
              />
            </ArgonBox>

            {/* Password */}
            <ArgonBox mb={3}>
              <ArgonInput
                name="password"
                type="password"
                placeholder="Password (8+ characters)"
                value={formData.password}
                onChange={handleInputChange}
                size="large"
              />
            </ArgonBox>

            {/* Terms */}
            <ArgonBox display="flex" alignItems="center" mb={3}>
              <Checkbox
                checked={formData.agreeTerms}
                onChange={handleCheckboxChange}
                sx={{ p: 0, mr: 1 }}
              />
              <ArgonTypography variant="caption" color="text">
                I agree to the&nbsp;
                <ArgonTypography
                  component="a"
                  href="https://prmcore.com/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  fontWeight="bold"
                  color="info"
                >
                  Terms &amp; Privacy Policy
                </ArgonTypography>
              </ArgonTypography>
            </ArgonBox>

            {/* Submit */}
            <ArgonBox mb={2}>
              <ArgonButton
                variant="gradient"
                color="dark"
                fullWidth
                type="submit"
                disabled={loading}
                size="large"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </ArgonButton>
            </ArgonBox>

            {/* Sign In Link */}
            <ArgonBox textAlign="center" mt={3}>
              <ArgonTypography variant="button" color="text" fontWeight="regular">
                Already have an account?&nbsp;
                <ArgonTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  color="dark"
                  fontWeight="bold"
                  textGradient
                >
                  Sign in
                </ArgonTypography>
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      </Card>
    </CoverLayout>
  );
}

export default EnhancedSignUp;
